import { ReactNode } from 'react';
import * as S from '~/components/CreateNewCharacter/DetailInput/DetailInput.styled';
import { DefaultButton, Checkbox, FlexStack, Icon } from '~/components/atomic';
import { Textarea } from '~/components/common';
import { CommonInput } from '~/hooks/CreateNewCharacter/useDetailInput';

interface SetProfileViewProps {
  children?: ReactNode;
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  profileImage: string | undefined;
  handleDeleteImageClick: () => void;
  handleUserNameClick: () => void;
  handleDirectionClick: () => void;
  textRef: React.RefObject<HTMLTextAreaElement>;
  commonInput: CommonInput;
  showPersona: boolean;
  setShowPersona: (state: boolean) => void;
  setCommonInput: React.Dispatch<React.SetStateAction<CommonInput>>;
  handleApplyClick: () => void;
  isPending: boolean;
  GenerateButtonDisabled: boolean;
}

export default function SetProfileView({
  children,
  handleImageChange,
  profileImage,
  commonInput,
  showPersona,
  setShowPersona,
  handleDeleteImageClick,
  setCommonInput,
  handleUserNameClick,
  handleDirectionClick,
  textRef,
  handleApplyClick,
  isPending,
  GenerateButtonDisabled,
}: SetProfileViewProps) {
  const { characterName, opening, tagLine, persona } = commonInput;

  return (
    <S.Container>
      <FlexStack spacing={24}>
        <FlexStack>
          <S.DetailTitle>Profile image</S.DetailTitle>
          <S.UploadImageWrapper>
            <S.UploadImageLabel>
              <input
                type='file'
                accept='.jpeg, .jpg, .png'
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
              <S.ProfileEditWrapper>
                {profileImage ? (
                  <>
                    <img src={profileImage} alt='profile' />
                    <S.ExpressionDeleteButton
                      size={24}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImageClick();
                      }}
                    >
                      <Icon iconName='Close' size={18} />
                    </S.ExpressionDeleteButton>
                  </>
                ) : (
                  <Icon iconName='Add' size={28} />
                )}
              </S.ProfileEditWrapper>
            </S.UploadImageLabel>
          </S.UploadImageWrapper>
        </FlexStack>

        <FlexStack>
          <S.DetailTitle>Character name</S.DetailTitle>
          <Textarea
            placeholder='Character name'
            value={characterName}
            onChange={(e) =>
              setCommonInput((prev) => ({
                ...prev,
                characterName: e.target.value.replaceAll('\n', ''),
              }))
            }
            maxLength={20}
            rows={1}
          />
        </FlexStack>

        <FlexStack>
          <S.DetailTitle>Opening</S.DetailTitle>

          <Textarea
            ref={textRef}
            value={opening}
            onChange={(e) =>
              setCommonInput((prev) => ({
                ...prev,
                opening: e.target.value,
              }))
            }
            maxLength={2000}
            placeholder='Hi, nice to meet you! *direction:Shaking one’s hand*'
          />
          <FlexStack direction='row'>
            <S.OpeningButton
              className='direction'
              onClick={handleDirectionClick}
            >
              <div>
                <Icon size={28} iconName='AddCircle' color='content_dark_1' />
                <span>Direction</span>
              </div>
            </S.OpeningButton>
            <S.OpeningButton
              className='user_name'
              onClick={handleUserNameClick}
            >
              <div>
                <Icon size={28} iconName='AddCircle' color='content_dark_1' />
                <span>User name</span>
              </div>
            </S.OpeningButton>
          </FlexStack>
        </FlexStack>

        <FlexStack>
          <S.DetailTitle>Tagline</S.DetailTitle>
          <Textarea
            value={tagLine}
            onChange={(e) =>
              setCommonInput((prev) => ({
                ...prev,
                tagLine: e.target.value,
              }))
            }
            maxLength={45}
            placeholder='Add character tagline.'
            rows={2}
          />
        </FlexStack>

        <FlexStack>
          <S.DetailTitle>Persona</S.DetailTitle>

          <S.ContentSubDesc>
            ▶︎ Feel free to describe the character, including their role,
            backstory, and other relevant details.
          </S.ContentSubDesc>
          <Textarea
            value={persona}
            onChange={(e) =>
              setCommonInput((prev) => ({
                ...prev,
                persona: e.target.value,
              }))
            }
            maxLength={2000}
            rows={13}
            placeholder='This version only supports English.'
          />
          <ul>
            <S.CheckList>
              <Checkbox
                id='CreateCharacterShowPersona'
                checked={showPersona}
                onChange={(e) => setShowPersona(e.target.checked)}
                colorType={'blue'}
              />
              <S.Terms htmlFor='CreateCharacterShowPersona'>
                <span>Show character details to the public</span>
              </S.Terms>
            </S.CheckList>
          </ul>
        </FlexStack>

        <FlexStack>
          <DefaultButton
            onClick={handleApplyClick}
            disabled={isPending || GenerateButtonDisabled}
          >
            {isPending ? 'Applying...' : 'Apply'}
          </DefaultButton>
          {children}
        </FlexStack>
      </FlexStack>
    </S.Container>
  );
}
